// THE LINE-ITEM BULK BAR, AS DATA (owner, 2026-09-29 — `docs/decisions.md`
// D-228 to D-232). The arithmetic and the plans behind `LineItemBulkBar.tsx`,
// kept out of the components so the tests can call them.
//
// *"In the invoice modal, users need a way to bulk-apply Account and
// Department values … An Account would be necessary to enable that button, but
// a Department would not be."* And: *"maybe add an option for the user to
// choose between applying those selections to existing selected rows and
// collapsing all selected rows into one new row with the selected Account and
// Department values … use averages or sums or whatever when collapsing Price
// and Quantity so the Total does not change by default."*
//
// Two modes. APPLY writes the chosen Account, and the Department if one was
// chosen, onto every selected line, through the write each line's own pickers
// use. COMBINE, at Import Control only, replaces the selected lines with one
// new line. Its Total is the sum of the selected totals, exactly, to the cent;
// its Price is that Total and its Quantity 1, all three locked (D-242).
//
// DEPARTMENT-ONLY APPLY (owner, 2026-10-07 — D-305). Where the route lock
// leaves a line's Department open and its Account locked — the Manager route,
// from Manager Approval through Manual Entry — Apply writes the chosen
// Department alone and never the Account (`bulkApplyScope`). The bulk row
// exists because approval is refused until every line is answered (D-228);
// locking the Account must not send those steps back to one picker per line.
//
// THE BAR IS A ROW OF THE TABLE (owner, 2026-09-30 — D-242): *"I would like
// the Description, Account, Department, Price, Quantity, and Total values to
// sit above where they normally sit, as closely as possible."* Every field
// shows once a line is checked; the ones the mode does not use are disabled
// (`bulkFieldsEnabled`).

import { useEffect, useMemo, useState } from 'react'
import { lineTotal } from './format'
import type { LineItemState } from './intakeSaveBody'
import type { AssociatedEntity } from './types'

export type BulkMode = 'apply' | 'combine'

/** The mode picker's two options (D-242: *"the renamed Apply/Combine picker"*).
    The first reads Update since 2026-10-14 (owner), so that Apply can be the
    button; its stored value stays `apply`. */
export const BULK_MODE_OPTIONS: readonly { value: BulkMode; label: string }[] = [
  { value: 'apply', label: 'Update' },
  { value: 'combine', label: 'Combine' },
]

/**
 * WHAT APPLY MAY WRITE, from which halves of a line's GL code are open here
 * (`glCodeEditable`, `lib/required.ts`):
 *
 * - `'account'`: the Account is open (Import Control, Controller Approval).
 *   Apply writes the Account, and the Department if one was chosen.
 * - `'department-only'`: the Department is open and the Account is not (the
 *   Manager route past Import Control — D-305). Apply writes the Department
 *   alone.
 * - `null`: neither is open. No boxes and no bulk row.
 */
export type BulkApplyScope = 'account' | 'department-only'

export function bulkApplyScope(open: { account: boolean; department: boolean }): BulkApplyScope | null {
  if (open.account) return 'account'
  return open.department ? 'department-only' : null
}

/** Which of the bulk row's fields each mode uses. The rest show, disabled (D-242). */
interface BulkFieldsEnabled {
  description: boolean
  account: boolean
  department: boolean
  /** Price, Quantity and Total: locked values in Combine, disabled in Apply. */
  amounts: boolean
  additionalDetail: boolean
  associatedEntities: boolean
}

export function bulkFieldsEnabled(mode: BulkMode, scope: BulkApplyScope = 'account'): BulkFieldsEnabled {
  const combine = mode === 'combine'
  return {
    description: combine,
    // Department-only Apply shows the Account disabled: it is not a choice (D-305).
    account: scope === 'account',
    department: true,
    amounts: combine,
    additionalDetail: combine,
    associatedEntities: combine,
  }
}

/**
 * IS COMBINE OFFERED? At Import Control only — the one step where lines are
 * added and removed (D-216) — and only once there are two lines to combine.
 * Everywhere else the bar applies and has no mode choice.
 */
export function combineOffered(atImportControl: boolean, selectedCount: number): boolean {
  return atImportControl && selectedCount >= 2
}

/** A money value in whole cents, or 0 for a blank or unreadable one. */
function cents(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0
  if (typeof value === 'string' && value.trim() === '') return 0
  const n = Number(value)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

/** The selected lines' totals added up in whole cents, as a two-place string. */
export function sumTotals(totals: readonly (number | string | null | undefined)[]): string {
  const sum = totals.reduce<number>((acc, t) => acc + cents(t), 0)
  return (sum / 100).toFixed(2)
}

/**
 * COMBINE'S VALUES: the Total is the sum of the selected totals, the Price is
 * that Total and the Quantity is 1 (owner, 2026-09-30 — D-242: *"keep the
 * Total equal to the sum of the selected rows' combined Total. Make Price
 * equal that new Total, and set Quantity to 1."*). All three are locked.
 * Summing in whole cents keeps it exact: $0.10 + $0.20 is $0.30 here, not
 * $0.30000000000000004.
 */
export function combineDefaults(totals: readonly (number | string | null | undefined)[]): {
  price: string
  quantity: string
  total: string
} {
  const price = sumTotals(totals)
  return { price, quantity: '1', total: lineTotal(price, '1') }
}

/**
 * WHAT THE BUTTON STILL NEEDS, as its tooltip says it — the same kind of
 * sentence Release's tooltip gives. `undefined` once nothing is missing.
 */
export function bulkBlockedReason(input: {
  mode: BulkMode
  accountId: string | undefined
  description: string
  /** Combine only: a Marketing Category the new line owes is empty (D-242). */
  additionalDetailMissing?: boolean
  /** Department-only Apply (D-305): the Department is what is required, and the Account is never asked for. */
  scope?: BulkApplyScope
  departmentId?: string
}): string | undefined {
  const missing: string[] = []
  if (input.scope === 'department-only') {
    if (!input.departmentId) missing.push('a Department')
  } else if (!input.accountId) missing.push('an Account')
  if (input.mode === 'combine') {
    // THE COMBINED LINE OWES A DEPARTMENT (owner, 2026-10-14: "when we
    // combine, we know that the outcome will be one row where Department will
    // be required"), as every line does at Import Control since D-309. Update
    // leaves it optional: the checked lines may need different Departments.
    if (input.scope !== 'department-only' && !input.departmentId) missing.push('a Department')
    if (input.description.trim() === '') missing.push('a Description')
    // The Price and Quantity are locked (D-242), so they are never missing.
    if (input.additionalDetailMissing) missing.push('the Additional Detail')
  }
  if (missing.length === 0) return undefined
  const list =
    missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(', ')}${missing.length > 2 ? ',' : ''} and ${missing[missing.length - 1]}`
  return `Choose ${list} first.`
}

/**
 * WHAT APPLY DOES TO ONE LINE'S DEPARTMENT — what a pick on the line's own
 * Account picker does (owner, 2026-09-30, D-242, replacing D-229's keep):
 *
 * - A Department was chosen: that one (`set`).
 * - None was chosen and the Account is not changing: nothing (`leave`).
 * - Otherwise: the Account write's own rule (`cascade`) — the Department is
 *   cleared, or filled where the new Account admits exactly one. A Department
 *   that would also fit the new Account is cleared too, as on a row.
 */
type ApplyDepartmentPlan =
  | { kind: 'set'; departmentId: string }
  | { kind: 'leave' }
  | { kind: 'cascade' }

export function departmentPlanForApply(input: {
  chosenDepartmentId: string | undefined
  currentAccountId: string | null | undefined
  currentDepartmentId: string | null | undefined
  newAccountId: string
}): ApplyDepartmentPlan {
  if (input.chosenDepartmentId) return { kind: 'set', departmentId: input.chosenDepartmentId }
  if ((input.currentAccountId ?? null) === input.newAccountId) return { kind: 'leave' }
  return { kind: 'cascade' }
}

/** The one Account every checked line holds, or `null` when they differ or any has none (D-242). */
export function sharedAccountId(accountIds: readonly (string | null | undefined)[]): string | null {
  const first = accountIds[0]
  if (!first) return null
  return accountIds.every((id) => id === first) ? first : null
}

/** An Apply or Combine with an Account: the chosen Account, and the Department if one was chosen. */
export interface AccountBulkChoice {
  accountId: string
  accountLabel: string
  departmentId?: string
  departmentLabel?: string
}

/** A Department-only Apply (D-305): the Department alone. No Account is written. */
interface DepartmentOnlyBulkChoice {
  accountId?: undefined
  departmentId: string
  departmentLabel: string
}

/** What the bar's Apply hands its caller. */
export type BulkChoice = AccountBulkChoice | DepartmentOnlyBulkChoice

/**
 * APPLY, AT IMPORT CONTROL: the editor's rows with the choice written onto the
 * selected ones. The editor's next save sends them, as it would a pick on each
 * row: a cascaded line's Department is cleared on screen, and the server
 * derives the sole one for the new Account where there is one. A
 * Department-only choice sets the Department and leaves the Account.
 */
export function applyToRows(
  rows: readonly LineItemState[],
  selectedIds: ReadonlySet<string>,
  choice: BulkChoice,
): LineItemState[] {
  return rows.map((r) => {
    if (!selectedIds.has(r.lineItemId)) return r
    if (choice.accountId === undefined) {
      return { ...r, departmentId: choice.departmentId, departmentLabel: choice.departmentLabel, departmentPicked: true }
    }
    const plan = departmentPlanForApply({
      chosenDepartmentId: choice.departmentId,
      currentAccountId: r.accountId,
      currentDepartmentId: r.departmentId,
      newAccountId: choice.accountId,
    })
    const account = { accountId: choice.accountId, accountLabel: choice.accountLabel }
    switch (plan.kind) {
      case 'set':
        return { ...r, ...account, departmentId: plan.departmentId, departmentLabel: choice.departmentLabel, departmentPicked: true }
      case 'leave':
        return { ...r, ...account }
      case 'cascade':
        return { ...r, ...account, departmentId: undefined, departmentLabel: undefined, departmentPicked: false }
    }
  })
}

/**
 * THE NEW LINE'S ADDITIONAL DETAIL, as chosen in the bulk row (D-242). Only
 * the sections the new line owes are named; each named one is sent, and
 * replaces what the server would otherwise carry over.
 */
export interface CombineDetails {
  marketingCategoryId?: string | null
}

/** What Combine needs beyond the Account and Department. Combine always names an Account. */
export interface CombineInput extends AccountBulkChoice {
  description: string
  price: string
  quantity: string
  details?: CombineDetails
}

/** One selected line's Additional Detail, as the bulk row reads them. */
export interface SelectedDetail {
  marketingCategoryId?: string | null
  marketingLabel?: string
}

/** The one value every selected line holds, or `null` — the server's `agreedValue`. */
function agreed<T>(values: readonly (T | null | undefined)[]): T | null {
  if (values.length === 0) return null
  const first = values[0]
  if (first === null || first === undefined) return null
  return values.every((v) => v === first) ? first : null
}

/**
 * THE PROPOSED LINE'S STARTING ADDITIONAL DETAIL: each value every selected
 * line agrees on — the rule the server's carry has always used
 * (`backend/src/domain/lineCombine.ts`) — so the row starts from what would
 * have landed. The reviewer fills in the rest before Combine is enabled.
 */
export function agreedDetails(lines: readonly SelectedDetail[]): {
  marketing: { id: string | null; label: string }
} {
  const marketingId = agreed(lines.map((l) => l.marketingCategoryId))
  return {
    marketing: { id: marketingId, label: marketingId ? (lines[0]?.marketingLabel ?? marketingId) : '—' },
  }
}

/** Which entity a tag names — the server's `entityIdentity`: a resolved tag by
    its reference, an unresolved one by its type and text, without case. */
function tagIdentity(
  e: Pick<AssociatedEntity, 'entity_type' | 'vehicle_id' | 'staff_upn' | 'entity_name' | 'entity_source_text'>,
): string {
  if (e.vehicle_id) return `${e.entity_type}:vehicle:${e.vehicle_id}`
  if (e.staff_upn) return `${e.entity_type}:staff:${e.staff_upn.trim().toLowerCase()}`
  return `${e.entity_type}:text:${(e.entity_name ?? e.entity_source_text ?? '').trim().toLowerCase()}`
}

/**
 * THE PROPOSED LINE'S ASSOCIATED ENTITIES (D-242: *"any Associated Entities
 * from a selected row should by default land in the proposed new row"*): every
 * active tag on a selected line, one per entity, in the order given — what the
 * server's `planEntityCopy` copies onto the new line.
 */
export function combinedEntities<T extends AssociatedEntity>(tags: readonly T[], lineIds: ReadonlySet<string>): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const t of tags) {
    if (t.is_active === false || !t.child_id || !lineIds.has(t.child_id)) continue
    const key = tagIdentity(t)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(t)
  }
  return out
}

/**
 * COMBINE, AT IMPORT CONTROL: the editor's rows with the selected lines
 * replaced by one new line, at the place of the first selected line, and the
 * saved ids to remove. The new line names the saved lines it replaces
 * (`combines`), so the server copies their tags and carries their agreed categories to it in
 * the same save (D-230, D-307). Its number is the next one after every line on screen;
 * the server moves it past any number a removed line still reserves.
 */
export function combineRows(
  rows: readonly LineItemState[],
  selectedIds: ReadonlySet<string>,
  input: CombineInput,
  newId: string,
): { rows: LineItemState[]; removedIds: string[] } {
  const selected = rows.filter((r) => selectedIds.has(r.lineItemId))
  const at = rows.findIndex((r) => selectedIds.has(r.lineItemId))
  if (selected.length === 0 || at < 0) return { rows: [...rows], removedIds: [] }
  const removedIds = selected.filter((r) => !r.isNew).map((r) => r.lineItemId)
  const sequence = rows.reduce((m, r) => Math.max(m, r.sequence ?? 0), 0) + 1
  const combined: LineItemState = {
    lineItemId: newId,
    clientKey: newId,
    sequence,
    description: input.description.trim(),
    quantity: input.quantity,
    price: input.price,
    total: lineTotal(input.price, input.quantity),
    accountId: input.accountId,
    accountLabel: input.accountLabel,
    departmentId: input.departmentId,
    departmentLabel: input.departmentLabel,
    departmentPicked: input.departmentId !== undefined,
    isNew: true,
    combines: removedIds,
    ...(input.details ? { combineDetails: { ...input.details } } : {}),
  }
  const kept = rows.filter((r) => !selectedIds.has(r.lineItemId))
  const before = rows.slice(0, at).filter((r) => !selectedIds.has(r.lineItemId)).length
  return { rows: [...kept.slice(0, before), combined, ...kept.slice(before)], removedIds }
}

/** Joins line numbers as the bar's messages say them: "1", "1 and 2", "1, 2 and 3". */
function lineList(numbers: readonly (number | null | undefined)[]): string {
  const shown = numbers.map((n) => String(n ?? '—'))
  if (shown.length <= 1) return shown.join('')
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
}

/** One line APPLY writes past Import Control, and what it was when chosen. */
export interface ApplyLine {
  id: string
  line_number?: number | null
  row_version: number
  account_id?: string | null
  department_id?: string | null
  total?: number | string | null
}

/** The two per-line writes APPLY uses past Import Control — the ones the row pickers use. */
interface ApplyWrites {
  account: (line: ApplyLine, accountId: string) => Promise<{ applied: boolean; reason?: string; line?: ApplyLine }>
  department: (line: ApplyLine, departmentId: string) => Promise<{ applied: boolean; reason?: string; line?: ApplyLine }>
}

/**
 * APPLY, PAST IMPORT CONTROL. There is no bulk route for the Account, so each
 * line gets the same writes its own pickers make, one line at a time: the
 * Account when it changes, then the Department when the plan names one the
 * line does not already hold. A Department-only choice (D-305) makes the
 * Department write alone and never calls the Account route. Each write records
 * its own correction, exactly as a one-by-one edit does. It stops at the first
 * refusal and says which lines landed, which one was refused and why, and
 * which were not reached.
 */
export async function applyLinesInTurn(
  lines: readonly ApplyLine[],
  choice: BulkChoice,
  writes: ApplyWrites,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const done: ApplyLine[] = []
  for (const [i, original] of lines.entries()) {
    let line = original
    const plan: ApplyDepartmentPlan =
      choice.accountId === undefined
        ? { kind: 'set', departmentId: choice.departmentId }
        : departmentPlanForApply({
            chosenDepartmentId: choice.departmentId,
            currentAccountId: line.account_id,
            currentDepartmentId: line.department_id,
            newAccountId: choice.accountId,
          })
    let refusal: string | undefined
    if (choice.accountId !== undefined && (line.account_id ?? null) !== choice.accountId) {
      const res = await writes.account(line, choice.accountId)
      if (!res.applied) refusal = res.reason ?? 'The change was rejected.'
      else if (res.line) line = res.line
    }
    if (refusal === undefined && plan.kind === 'set' && line.department_id !== plan.departmentId) {
      const res = await writes.department(line, plan.departmentId)
      if (!res.applied) refusal = res.reason ?? 'The change was rejected.'
    }
    if (refusal !== undefined) {
      const rest = lines.slice(i + 1)
      const parts = [
        done.length > 0 ? `Applied to line${done.length === 1 ? '' : 's'} ${lineList(done.map((l) => l.line_number))}.` : '',
        `Line ${original.line_number ?? '—'} was refused: ${refusal.replace(/\.$/, '')}.`,
        rest.length > 0
          ? `Line${rest.length === 1 ? '' : 's'} ${lineList(rest.map((l) => l.line_number))} ${rest.length === 1 ? 'was' : 'were'} not changed.`
          : '',
      ]
      return { ok: false, message: parts.filter(Boolean).join(' ') }
    }
    done.push(original)
  }
  return { ok: true }
}

/**
 * THE SELECTED LINES, by id. Resets when `resetKey` changes (the invoice), and
 * prunes itself to `liveIds` whenever the lines reload, so a removed line is
 * never sent.
 */
export function useLineSelection(liveIds: readonly string[], resetKey: string | undefined) {
  const [selected, setSelected] = useState<readonly string[]>([])
  const liveKey = liveIds.join('|')
  useEffect(() => {
    setSelected([])
  }, [resetKey])
  useEffect(() => {
    const live = new Set(liveKey.split('|'))
    setSelected((prev) => (prev.every((id) => live.has(id)) ? prev : prev.filter((id) => live.has(id))))
  }, [liveKey])
  const selectedSet = useMemo(() => new Set(selected), [selected])
  return {
    selectedIds: selectedSet,
    count: selected.length,
    /** The header box: checked when every line is, mixed when some are. */
    headerState: headerCheckState(selected.length, liveIds.length),
    toggle: (id: string, on: boolean) =>
      setSelected((prev) => (on ? [...prev.filter((x) => x !== id), id] : prev.filter((x) => x !== id))),
    setAll: (on: boolean) => setSelected(on ? [...liveIds] : []),
    clear: () => setSelected([]),
  }
}

/** The header box's state for `count` of `total` selected. */
export function headerCheckState(count: number, total: number): boolean | 'mixed' {
  if (count > 0 && count === total) return true
  return count > 0 ? 'mixed' : false
}
